import { Box, Link, styled } from '@mui/material';

import appStrings from '@/assets/constants/appStrings-no.ts';

import ArrowIcon from '../../../assets/images/arrow.svg?react';
import LinesIcon from '../../../assets/images/lines_2.svg?react';
import PhoneIcon from '../../../assets/images/phone.svg?react';
import RefinancingIcon from '../../../assets/images/products/refinancing_icon.svg?react';

const ContactContent = styled('div')(({ theme }) => ({
  color: theme.palette.text.default,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.up(700)]: {
    marginBottom: theme.spacing(4),
    maxWidth: 750,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
}));

const Subtitle = styled('h2')(({ theme }) => ({
  position: 'relative',
  fontWeight: theme.typography.fontWeightSemiBold,
  fontSize: '1.25rem',
  marginBottom: theme.spacing(6),
  [theme.breakpoints.up('sm')]: {
    fontSize: '1.75rem',
  },
  [theme.breakpoints.up(700)]: {
    marginBottom: theme.spacing(10),
  },
}));

const StyledArrowIcon = styled(ArrowIcon)(({ theme }) => ({
  '& > path': { stroke: '#E8F0F8' },
  position: 'absolute',
  bottom: -27,
  left: -85,
  height: 30,
  transform: 'rotate(90deg)',
  [theme.breakpoints.up(700)]: {
    height: 40,
    bottom: -55,
    left: 'unset',
    right: -100,
    transform: 'rotate(-78deg) scaleX(-1)',
  },
}));

const StyledLinesIcon = styled(LinesIcon)(({ theme }) => ({
  position: 'absolute',
  bottom: -10,
  width: 100,
  height: 10,
  right: 0,
  [theme.breakpoints.up(700)]: {
    bottom: -10,
    width: 140,
    height: 15,
  },
}));

const ContactItemsContainer = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
  [theme.breakpoints.up(700)]: {
    marginBottom: 0,
    order: 2,
    width: '45%',
  },
}));

const ContactItemContainer = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  [theme.breakpoints.up(700)]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3, 4),
  },
}));

const ContactIconContainer = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  background: theme.palette.background.primary,
  marginRight: theme.spacing(2.5),
  height: theme.spacing(4.5),
  width: theme.spacing(4.5),
  minWidth: theme.spacing(4.5),
  [theme.breakpoints.up(700)]: {
    height: theme.spacing(7),
    width: theme.spacing(7),
    minWidth: theme.spacing(7),
  },
}));

const ContactIcon = styled('svg')(({ theme }) => ({
  '& > path': {
    stroke: theme.palette.common.white,
  },
  width: 20,
  height: 20,
  [theme.breakpoints.up(700)]: {
    height: 25,
    width: 25,
  },
}));

const ContactLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.default,
  ...theme.typography.p2SemiBold,
}));

const ContactTitle = styled('p')(({ theme }) => ({
  display: 'flex',
  color: theme.palette.text.default,
  ...theme.typography.p2SemiBold,
  fontWeight: 'bold !important',
}));

const ContactContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
});

const OpenHoursContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  padding: theme.spacing(1),
  order: 3,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(2, 3),
    background: '#F7FBFF',
    width: '53%',
    order: 1,
    justifyContent: 'space-between',
  },
}));

const OpenHoursTitle = styled('p')(({ theme }) => ({
  paddingTop: theme.spacing(2),
  fontWeight: theme.typography.fontWeightBold,
  fontSize: '1.188rem',
  [theme.breakpoints.up(700)]: {
    fontSize: '1.375rem',
  },
}));

const OpenHoursText = styled('span')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  marginTop: theme.spacing(2),
  '&:first-child': { marginTop: theme.spacing(3.5) },
  '& > :last-child': { color: theme.palette.primary.main },
}));

const contactItems = [
  {
    Icon: PhoneIcon,
    href: `tel:${appStrings.CONTACT.PHONE2}`,
    text: appStrings.CONTACT.PHONE1,
  },
  {
    Icon: RefinancingIcon,
    href: `mailto:${appStrings.CONTACT.REFINANCING_QUESTIONS_EMAIL}`,
    text: appStrings.CONTACT.REFINANCING_QUESTIONS_EMAIL,
    title: appStrings.CONTACT.REFINANCING_QUESTIONS_EMAIL_TITLE,
  },
];

const ContactInfo = () => {
  return (
    <>
      <Box
        sx={{
          my: 2,
        }}
      >
        <h1 className="typography-v1-legacy-h1">{appStrings.CONTACT.HELP}</h1>
      </Box>
      <Subtitle>
        {appStrings.CONTACT.TITLE}
        <StyledArrowIcon />
        <StyledLinesIcon />
      </Subtitle>
      <ContactContent>
        <ContactItemsContainer>
          {contactItems.map(({ href, Icon, text, title }) => (
            <ContactItemContainer key={text}>
              <ContactIconContainer>
                <ContactIcon as={Icon} />
              </ContactIconContainer>
              <ContactContainer>
                {title ? <ContactTitle>{title}</ContactTitle> : null}
                <ContactLink href={href}>{text}</ContactLink>
              </ContactContainer>
            </ContactItemContainer>
          ))}
        </ContactItemsContainer>
        <OpenHoursContainer>
          <OpenHoursTitle>{appStrings.CONTACT.OPENING_CUSTOMER_SERVICE_TITLE}</OpenHoursTitle>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
              }}
            >
              {appStrings.CONTACT.OPENING.REGULAR.items.map(({ day, hours }) => (
                <OpenHoursText key={day}>
                  <p className="typography-v1-legacy-p2">{day}</p>
                  <p className="typography-v1-legacy-p2SemiBold">{hours}</p>
                </OpenHoursText>
              ))}
            </Box>
            {appStrings.CONTACT.OPENING.HOLIDAYS.display ? (
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <p className="typography-v1-h4">{appStrings.CONTACT.OPENING.HOLIDAYS.title}</p>
                {appStrings.CONTACT.OPENING.HOLIDAYS.items.map(({ day, hours }) => (
                  <OpenHoursText key={day}>
                    <p className="typography-v1-legacy-p2">{day}</p>
                    <p className="typography-v1-legacy-p2SemiBold">{hours}</p>
                  </OpenHoursText>
                ))}
              </Box>
            ) : null}
          </Box>
        </OpenHoursContainer>
      </ContactContent>
    </>
  );
};

export default ContactInfo;
